import { useState } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Calendar, Users, Heart, Briefcase, Award, Send, Check } from "lucide-react";

export default function Catering() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", event_type: "wedding",
    event_date: "", guests: 50, budget: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/catering", { ...form, guests: parseInt(form.guests) });
      setSubmitted(true);
      toast.success(lang === "fr" ? "Demande envoyée · L'équipe vous recontacte sous 24h" : "Request sent · Team will reply within 24h");
    } catch {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20" data-testid="catering-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="eyebrow text-[var(--dsf-orange)] mb-3">{t("catering.eyebrow")}</div>
            <h1 className="font-serif text-5xl md:text-7xl text-[var(--dd-green)] leading-[1.02] mb-6">{t("catering.title")}</h1>
            <p className="text-[var(--dsf-text-secondary)] text-lg leading-relaxed mb-8">{t("catering.subtitle")}</p>
            <div className="grid grid-cols-3 gap-6">
              {[
                { v: "200+", l: lang === "fr" ? "Événements/an" : "Events/year" },
                { v: "50K", l: lang === "fr" ? "Convives" : "Guests" },
                { v: "98%", l: "Satisfaction" },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-serif text-4xl font-semibold text-[var(--dsf-orange)]">{s.v}</div>
                  <div className="text-xs uppercase tracking-wider text-[var(--dsf-text-muted)] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={200}>
            <div className="aspect-[4/5] max-w-sm mx-auto overflow-hidden">
              <img src="https://images.unsplash.com/photo-1530062845289-9109b2c9c868?w=900" alt="" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 dd-bg-bone text-[var(--dd-green)] px-6 md:px-12 lg:px-20" data-testid="catering-services">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3">Services</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-12 max-w-2xl">{lang === "fr" ? "Quatre formats d'exception, zéro compromis." : "Four exceptional formats, zero compromise."}</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Heart, fr: "Mariages", en: "Weddings", desc_fr: "De 50 à 500 convives, menus signature.", desc_en: "From 50 to 500 guests, signature menus." },
            { icon: Briefcase, fr: "Entreprise", en: "Corporate", desc_fr: "Cocktails, séminaires, dîners de gala.", desc_en: "Cocktails, seminars, gala dinners." },
            { icon: Calendar, fr: "Anniversaires", en: "Birthdays", desc_fr: "Célébrations privées sur mesure.", desc_en: "Custom private celebrations." },
            { icon: Award, fr: "Prestige", en: "Prestige", desc_fr: "Diplomatique, ambassades, VIP.", desc_en: "Diplomatic, embassies, VIP." },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="border border-black/8 p-7 hover:border-[var(--dsf-orange)] transition-colors h-full" data-testid={`service-${i}`}>
                <s.icon size={24} className="text-[var(--dsf-orange)] mb-5" />
                <div className="font-serif text-2xl font-semibold mb-2">{lang === "fr" ? s.fr : s.en}</div>
                <p className="text-sm text-[var(--dsf-text-secondary)]">{lang === "fr" ? s.desc_fr : s.desc_en}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-6 md:px-12 lg:px-20">
        <Reveal>
          <div className="text-center mb-16">
            <div className="eyebrow text-[var(--dsf-orange)] mb-3">{lang === "fr" ? "Notre processus" : "Our process"}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[var(--dd-green)]">{lang === "fr" ? "De l'idée à l'événement" : "From idea to event"}</h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {[
            { fr: "Brief", en: "Brief", desc_fr: "Échange initial, vos besoins, votre vision." },
            { fr: "Devis", en: "Quote", desc_fr: "Proposition menu et chiffrage sous 48h." },
            { fr: "Dégustation", en: "Tasting", desc_fr: "Validation des plats avant l'événement." },
            { fr: "Service", en: "Service", desc_fr: "Brigade dédiée, exécution irréprochable." },
          ].map((p, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="text-center" data-testid={`process-${i}`}>
                <div className="w-12 h-12 mx-auto bg-[var(--dsf-orange)] text-[var(--dd-green)] flex items-center justify-center font-serif text-xl font-semibold mb-4">{i + 1}</div>
                <div className="font-serif text-xl font-semibold text-[var(--dd-green)] mb-2">{lang === "fr" ? p.fr : p.en}</div>
                <p className="text-sm text-[var(--dsf-text-secondary)]">{p.desc_fr}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="py-24 bg-transparent px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <div className="eyebrow text-[var(--dsf-orange)] mb-3">{lang === "fr" ? "Devis" : "Quote"}</div>
              <h2 className="font-serif text-4xl md:text-5xl text-[var(--dd-green)]">{lang === "fr" ? "Demandez votre devis sur mesure" : "Request your custom quote"}</h2>
            </div>
          </Reveal>

          {submitted ? (
            <Reveal>
              <div className="bg-white border border-[var(--dsf-success)] p-12 text-center" data-testid="catering-success">
                <div className="w-14 h-14 mx-auto bg-[var(--dsf-success)]/15 flex items-center justify-center mb-5">
                  <Check size={28} className="text-[var(--dsf-success)]" />
                </div>
                <h3 className="font-serif text-3xl text-[var(--dd-green)] mb-3">{lang === "fr" ? "Demande reçue" : "Request received"}</h3>
                <p className="text-[var(--dsf-text-secondary)]">{lang === "fr" ? "L'équipe traiteur vous recontactera sous 24h ouvrées." : "Our catering team will reply within 24 working hours."}</p>
              </div>
            </Reveal>
          ) : (
            <form onSubmit={submit} className="card-light p-8 lg:p-12" data-testid="catering-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label={t("common.name")} value={form.name} onChange={(v) => setForm({...form, name: v})} required testid="catering-name" />
                <Field label={t("common.email")} type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} required testid="catering-email" />
                <Field label={t("common.phone")} value={form.phone} onChange={(v) => setForm({...form, phone: v})} required testid="catering-phone" />
                <Field label={lang === "fr" ? "Société (optionnel)" : "Company (optional)"} value={form.company} onChange={(v) => setForm({...form, company: v})} testid="catering-company" />

                <div>
                  <Label>{lang === "fr" ? "Type d'événement" : "Event type"}</Label>
                  <select value={form.event_type} onChange={(e) => setForm({...form, event_type: e.target.value})} className="w-full border border-black/5 px-4 py-3 mt-2 text-sm bg-white" data-testid="catering-event-type">
                    <option value="wedding">{lang === "fr" ? "Mariage" : "Wedding"}</option>
                    <option value="corporate">{lang === "fr" ? "Entreprise" : "Corporate"}</option>
                    <option value="birthday">{lang === "fr" ? "Anniversaire" : "Birthday"}</option>
                    <option value="other">{lang === "fr" ? "Autre" : "Other"}</option>
                  </select>
                </div>
                <Field label={lang === "fr" ? "Date" : "Date"} type="date" value={form.event_date} onChange={(v) => setForm({...form, event_date: v})} required testid="catering-date" />
                <Field label={lang === "fr" ? "Convives" : "Guests"} type="number" value={form.guests} onChange={(v) => setForm({...form, guests: v})} required testid="catering-guests" />
                <Field label={lang === "fr" ? "Budget (FCFA, optionnel)" : "Budget (FCFA, optional)"} value={form.budget} onChange={(v) => setForm({...form, budget: v})} testid="catering-budget" />
              </div>
              <div className="mt-5">
                <Label>{lang === "fr" ? "Décrivez votre projet" : "Describe your project"}</Label>
                <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required rows={5} className="w-full border border-black/5 px-4 py-3 mt-2 text-sm focus:border-[var(--dsf-green)]" data-testid="catering-message" />
              </div>
              <button type="submit" disabled={loading} className="btn-primary mt-8 disabled:opacity-50" data-testid="catering-submit">
                <Send size={14} /> {loading ? t("common.loading") : (lang === "fr" ? "Envoyer ma demande" : "Send my request")}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Label = ({ children }) => <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] block">{children}</label>;
const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <Label>{label}</Label>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-black/5 px-4 py-3 mt-2 text-sm focus:border-[var(--dsf-green)] bg-white" data-testid={testid} />
  </div>
);
